import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function ContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const subjects = ['استفسار عام', 'مشكلة تقنية', 'اقتراح', 'شكوى', 'أخرى'];

  // الباك‑إند يشترط حقل type بقيم محددة — نشتقّه من الموضوع المختار
  const subjectTypeMap: Record<string, string> = {
    'استفسار عام': 'general',
    'مشكلة تقنية': 'login_issue',
    'اقتراح': 'suggestion',
    'شكوى': 'general',
    'أخرى': 'general',
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject || !form.message.trim()) {
      setError('يرجى تعبئة جميع الحقول المطلوبة');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/contact', { ...form, type: subjectTypeMap[form.subject] || 'general' });
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setError('حدث خطأ أثناء الإرسال. يرجى المحاولة لاحقاً.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✅</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">تم إرسال رسالتك بنجاح</h2>
          <p className="text-gray-500 mb-6">شكراً لتواصلك معنا. سنرد عليك في أقرب وقت ممكن.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => setSuccess(false)} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold text-sm transition">
              إرسال رسالة أخرى
            </button>
            <button onClick={() => navigate('/')} className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold text-sm transition">
              العودة للرئيسية
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" dir="rtl">
      <div className="max-w-2xl mx-auto space-y-6">

        <div className="text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📬</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">تواصل معنا</h1>
          <p className="text-gray-500 mt-2">نسعد بتواصلك معنا. أرسل لنا رسالتك وسنرد عليك بأسرع وقت.</p>
        </div>

        <div className="grid grid-cols-1 max-w-sm mx-auto gap-4">
          {[
            { icon: '📧', title: 'البريد الإلكتروني', value: 'support@aljalab.com' },
          ].map((item) => (
            <div key={item.title} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <span className="text-2xl mb-2 block">{item.icon}</span>
              <p className="text-xs text-gray-500 mb-1">{item.title}</p>
              <p className="text-sm font-semibold text-gray-900" dir="ltr">{item.value}</p>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-5">
          <h2 className="font-bold text-gray-900 text-lg">أرسل لنا رسالة</h2>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2">
              <span>⚠️</span> {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">الاسم <span className="text-red-500">*</span></label>
              <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="أدخل اسمك"
                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">البريد الإلكتروني <span className="text-red-500">*</span></label>
              <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="example@email.com" dir="ltr"
                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm text-left" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">رقم الجوال <span className="text-gray-400 text-xs">(اختياري)</span></label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="05XXXXXXXX" dir="ltr"
                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm text-left" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">الموضوع <span className="text-red-500">*</span></label>
              <select name="subject" value={form.subject} onChange={handleChange}
                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm bg-white">
                <option value="">اختر الموضوع</option>
                {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">الرسالة <span className="text-red-500">*</span></label>
            <textarea name="message" value={form.message} onChange={handleChange} placeholder="اكتب رسالتك هنا..." rows={5}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm resize-none" />
          </div>

          <button type="submit" disabled={loading}
            className="w-full h-12 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-xl font-bold text-base transition flex items-center justify-center gap-2">
            {loading ? (
              <>
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                جاري الإرسال...
              </>
            ) : 'إرسال الرسالة'}
          </button>
        </form>

        <div className="text-center pb-4">
          <button onClick={() => navigate(-1)} className="text-primary-500 hover:text-primary-700 font-semibold text-sm hover:underline">
            ← الرجوع
          </button>
        </div>

      </div>
    </div>
  );
}
